import { ModalTypeEnum, PageTitle, useAlert, useFilters, useModal } from "@wit/mpesa-ui-components";
import { AlertTypeEnum } from "@wit/mpesa-ui-components/lib/context/alert/alert.context";
import { Breadcrumb } from "@wit/mpesa-ui-components/lib/types";
import { TFunction } from "i18next";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { IStoreInterface } from "../../../configs/store.config";
import { RoutesEnum } from "../../../routes/routes.constants";
import { LoadingText, PageContainer, Row } from "../../../shared/shared.styled";
import ProductRecommendationsFilters from "./components/product-recommender-filters.component";
import ProductRecommendationsList from "./components/product-recommender-list.component";
import ProductRecommendationsApi from "./product-recommender.api";
import { ProductRecommendationsActions } from "./product-recommender.store";
import { filterProductRecommendations } from "./product-recommender.utils";

/**
 * Gets the Breadcrumbs
 * @param t
 */
const getBreadcrumbs = (t: TFunction): Breadcrumb[] => {
  return [
    {
      label: t("pages.configuration.title"),
      url: RoutesEnum.CONFIGS,
    },
    {
      label: t("pages.productRecommendations.productRecommendations"),
      url: "",
    },
  ];
};

/**
 * ProductRecommendationsPage component
 */
const ProductRecommendationsPage = () => {
  const [t] = useTranslation();
  const [showAlert, , setAlertProps] = useAlert();
  const dispatch = useDispatch();
  const history = useHistory();

  const [filtersResult, setFiltersResult] = useState<any>();

  const {
    filters,
    updateFilter,
    isFilterActive,
    clearFilter,
    resetFilters,
    getFilterValue,
    getFiltersQueryString,
    updateMultipleFilters,
  } = useFilters();

  const [showConfirmationModal, hideConfirmationModal, setConfirmationProps] = useModal(
    ModalTypeEnum.ConfirmationModal,
  );

  // Redux state
  const productRecommendationsStore = useSelector(
    (state: IStoreInterface) => state.productRecommendationsReducer.productRecommendations,
    shallowEqual,
  );

  const [selectedLanguage, setSelectedLanguage] = React.useState({ label: t("commons.languages.en"), key: "en" });

  /**
   * Gets the list of product recommendations
   */
  const getProductRecommendations = () => {
    ProductRecommendationsApi.methods.getProductRecommendations().then(
      res => {
        dispatch(ProductRecommendationsActions.creators.getProductRecommendationsSuccess(res.data));
      },
      () => {
        setAlertProps({
          title: t("pages.productRecommendations.getProductRecommendationsError"),
          type: AlertTypeEnum.ERROR,
        });
        showAlert();
      },
    );
  };

  /*
   * Get product recommendations
   * */
  React.useEffect(() => {
    getProductRecommendations();
  }, [dispatch]);

  /**
   * Delete Product Recommendation
   * @param id
   */
  const deleteProductRecommendation = (id: string) => {
    ProductRecommendationsApi.methods.deleteProductRecommendation(id).then(
      () => {
        setAlertProps({
          title: t("pages.productRecommendations.deleteSuccess"),
          type: AlertTypeEnum.SUCCESS,
        });
        showAlert();
        hideConfirmationModal();
        getProductRecommendations();
      },
      () => {
        setAlertProps({
          title: t("pages.productRecommendations.deleteError"),
          type: AlertTypeEnum.ERROR,
        });
        showAlert();
      },
    );
  };

  /**
   * Sets showable product recommendations
   * @param e
   */
  const setShowableProductRecommendations = (e: any) => {
    const newProduct = {
      productRecommendations: filterProductRecommendations(e, filters, selectedLanguage.key),
    };
    dispatch(
      ProductRecommendationsActions.creators.getProductRecommendationsSuccess({
        ...productRecommendationsStore,
        ...newProduct,
      }),
    );
  };

  React.useEffect(() => {
    if (filters.size > 0) {
      const newProduct = {
        productRecommendations: filterProductRecommendations(
          productRecommendationsStore.productRecommendations ? productRecommendationsStore.productRecommendations : [],
          filters,
          selectedLanguage.key,
        ),
      };
      setFiltersResult(newProduct);
    } else {
      setFiltersResult(undefined);
    }
  }, [filters, productRecommendationsStore]);

  return (
    <PageContainer>
      <PageTitle
        breadcrumbs={getBreadcrumbs(t)}
        title={t("pages.productRecommendations.productRecommendations")}
        navigateFunction={history.push}
        goBackFn={() => history.push(RoutesEnum.CONFIGS)}
        separatorStyle={{ display: "none" }}
        titleContainerStyle={{ marginTop: "13px", marginBottom: "20px", height: "60px" }}
        rightComponentContainerStyle={{ display: "flex", alignContent: "center", height: "100%", marginRight: "0px" }}
      />

      {!productRecommendationsStore.productRecommendations ? (
        <LoadingText>{t("commons.loadingResults")}</LoadingText>
      ) : null}
      {productRecommendationsStore.productRecommendations ? (
        <ProductRecommendationsContainer>
          <Row>
            <ProductRecommendationsFilters
              filters={filters}
              isFilterActive={isFilterActive}
              updateFilter={updateFilter}
              clearFilter={clearFilter}
              resetFilters={resetFilters}
              getFilterValue={getFilterValue}
              getFiltersQueryString={getFiltersQueryString}
              updateMultipleFilters={updateMultipleFilters}
            ></ProductRecommendationsFilters>
          </Row>

          <ProductRecommendationsList
            productRecommendations={
              filtersResult ? filtersResult.productRecommendations : productRecommendationsStore.productRecommendations
            }
            setProductRecommendations={setShowableProductRecommendations}
            onLanguageChange={setSelectedLanguage}
            selectedLanguage={selectedLanguage}
            onDelete={deleteProductRecommendation}
          ></ProductRecommendationsList>
        </ProductRecommendationsContainer>
      ) : null}
    </PageContainer>
  );
};

const ProductRecommendationsContainer = styled("div")`
  margin-bottom: 34px;
  display: flex;
  flex-direction: column;
`;

export default ProductRecommendationsPage;
